//defs
import { ApiResponse } from "@core-utils/interfaces/Api";

//utils
import { GlobalHeaderState } from "./Headers";

//internal-deps
import { isNetworkError, isConnectionError, isAbortError } from "@swiggy-private/core-ui";
import { Err, Ok, Result } from "@swiggy-private/js-fp";

//helpers
import { showNetworkError } from "@core-utils/helpers/ApiHelper";

//constants
import { DEFAULT_API_RETRY_DELAY_TIME_IN_MS } from "@core-utils/constants/Api";
import { TEXTS } from "@core-utils/constants/TextDict";

export interface ApiCallParams {
    apiHost: string;
    endpoint: string;
    searchParams?: { [key: string]: string };
    bodyParams?: { [key: string]: unknown };
    options?: RequestInit;
    timeout?: number;
    retry?: boolean;
    retryCount?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    interceptorCb?: (...args: any[]) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errorCb?: (...args: any[]) => void;
    shouldShowNetworkError?: boolean;
    excludeDefaultHeaders?: boolean;
}

async function sleep(ms: number): Promise<unknown> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function makeApiCall<T>(params: ApiCallParams): Promise<ApiResponse<T>> {
    const { retry = true, retryCount = 2, errorCb, interceptorCb } = params;
    const headers = { "Content-Type": "application/json", ...GlobalHeaderState.getHeaders() };
    const timeoutValue = params.timeout ?? 20000;
    const options: RequestInit = {
        ...params.options,
        headers: params.options?.headers ? { ...headers, ...params.options.headers } : headers,
    };

    if (params.bodyParams) {
        options.body = JSON.stringify(params.bodyParams);
    }

    const searchParams = params.searchParams;
    let url = `${params.apiHost}${params.endpoint}`;
    if (searchParams) {
        url += `?${new URLSearchParams(searchParams).toString()}`;
    }

    const startTime = Date.now();

    let timeoutRef = null;

    if (typeof AbortController !== "undefined") {
        const controller = new AbortController();
        const signal = controller.signal;
        options.signal = signal;

        timeoutRef = setTimeout(() => {
            const diff = Date.now() - startTime;
            if (diff >= timeoutValue) {
                controller.abort();
            }
        }, timeoutValue);
    }

    const _url = url.toString();
    const _logUrl = _url.replace(params.apiHost, "");

    try {
        const response = await fetch(_url, {
            method: "GET",
            mode: "cors",
            headers,
            ...options,
        });

        timeoutRef && clearTimeout(timeoutRef);

        if (!response.ok) {
            throw response;
        }

        const responseJson = await response.json();
        if (typeof interceptorCb === "function") {
            interceptorCb({ startTime, url: _logUrl, opts: options }, responseJson);
        }
        return responseJson;
    } catch (err) {
        timeoutRef && clearTimeout(timeoutRef);
        if ((isNetworkError(err) || isConnectionError(err) || isAbortError(err)) && retry && retryCount > 0) {
            await sleep(DEFAULT_API_RETRY_DELAY_TIME_IN_MS);
            return makeApiCall({ ...params, retryCount: retryCount - 1 });
        } else {
            if (typeof errorCb === "function") {
                errorCb({ startTime, url: _logUrl }, err);
            }

            // Show dialog for api failure due to network error
            showNetworkError(err);
            throw err;
        }
    }
}

export function unwrapResponse<T>(response?: ApiResponse<T>): Result<T, string> {
    if (response?.statusCode === 0) {
        return Ok(response.data as T);
    }

    return Err(response?.statusMessage || TEXTS.OOPS_SOMETHING_WENT_WRONG);
}

export function unwrapResponseWithCode<T>(
    response?: ApiResponse<T>,
    passApiDataInErrorPayload = false,
): Result<T, Pick<ApiResponse, "statusCode" | "statusMessage" | "statusTitle" | "ctaText" | "ctaAction">> {
    if (response?.statusCode === 0) {
        return Ok(response.data as T);
    }

    return Err({
        statusCode: response?.statusCode || 1,
        statusMessage: response?.statusMessage || TEXTS.OOPS_SOMETHING_WENT_WRONG,
        statusTitle: response?.statusTitle || "",
        ctaText: response?.ctaText || "",
        ctaAction: response?.ctaAction,
        ...(passApiDataInErrorPayload ? { data: response?.data } : {}),
    });
}
